import React from "react";
import styled from "styled-components";
import { useLanguage } from "../contexts/LanguageContext";
import { PROJECTS } from "../data/projects";

const PortfolioContainer = styled.div`
  padding: 50px;
`;

const BlockTitle = styled.h3`
  font-size: 20px;
  line-height: 26px;
  margin: 0 0 30px 0;
  font-weight: 500;
  text-transform: uppercase;
  color: #333;
`;

const FilterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 40px 0;
  text-align: center;

  li {
    display: inline-block;
    margin: 0 15px;
    padding: 10px 20px;
    cursor: pointer;
    text-transform: uppercase;
    font-weight: 700;
    border: 2px solid transparent;
    transition: all 0.3s;

    &.active {
      border-color: #f34452;
      color: #f34452;
    }

    &:hover {
      border-color: #f34452;
      color: #f34452;
    }
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
`;

const Project = styled.div<{ isVisible: boolean }>`
  position: relative;
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;
  cursor: pointer;
  display: ${(props) => (props.isVisible ? "block" : "none")};

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
  }
`;

const ProjectOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(243, 68, 82, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s;

  ${Project}:hover & {
    opacity: 1;
  }
`;

const ProjectInfo = styled.div`
  text-align: center;
  color: #fff;

  h4 {
    font-size: 20px;
    margin: 0 0 10px 0;
    text-transform: uppercase;
    font-weight: 700;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
`;

const EmailSection = styled.div`
  text-align: center;
  padding: 30px 0;

  h4 {
    color: #333;
    margin-bottom: 20px;
  }

  a {
    background: #f34452;
    color: #fff;
    padding: 15px 30px;
    text-decoration: none;
    text-transform: lowercase;
    font-weight: 700;

    &:hover {
      background: #d63946;
      color: #fff;
    }
  }
`;

interface ProjectData {
  id: string;
  title: string;
  description: string;
  image: string;
  categories: string[];
  component: string;
}

interface PortfolioContentProps {
  onProjectSelect?: (projectComponent: string) => void;
  activeFilter: string;
  onFilterChange: (filter: string) => void;
}

const PortfolioContent: React.FC<PortfolioContentProps> = ({
  onProjectSelect,
  activeFilter,
  onFilterChange,
}) => {
  const { t } = useLanguage();

  const projects: ProjectData[] = PROJECTS.map((p) => ({
    id: p.slug,
    title: t(`project.${p.slug}.title`),
    description: t(`project.${p.slug}.desc`),
    image: p.image,
    categories: [p.category],
    component: p.slug,
  }));

  const filters = [
    { key: "*", label: t("portfolio.filter.all") },
    { key: "work", label: t("portfolio.filter.work") },
    { key: "side", label: t("portfolio.filter.side") },
    { key: "school", label: t("portfolio.filter.school") },
  ];

  const isProjectVisible = (project: ProjectData) => {
    if (activeFilter === "*") return true;
    return project.categories.includes(activeFilter);
  };

  const handleProjectClick = (component: string) => {
    if (onProjectSelect) {
      onProjectSelect(component);
    }
  };

  return (
    <PortfolioContainer>
      <BlockTitle>{t("portfolio.title")}</BlockTitle>

      <FilterList>
        {filters.map((filter) => (
          <li
            key={filter.key}
            className={activeFilter === filter.key ? "active" : ""}
            onClick={() => onFilterChange(filter.key)}
          >
            {filter.label}
          </li>
        ))}
      </FilterList>

      <ProjectsGrid>
        {projects.map((project) => {
          const visible = isProjectVisible(project);
          return (
            <Project
              key={project.id}
              isVisible={visible}
              aria-hidden={!visible}
              tabIndex={visible ? 0 : -1}
              onClick={() => {
                if (visible) handleProjectClick(project.component);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && visible)
                  handleProjectClick(project.component);
              }}
              role="button"
              aria-label={`View ${project.title} project`}
            >
              <img loading="lazy" decoding="async" src={project.image} alt={project.title} />
              <ProjectOverlay>
                <ProjectInfo>
                  <h4>{project.title}</h4>
                  <p>{project.description}</p>
                </ProjectInfo>
              </ProjectOverlay>
            </Project>
          );
        })}
      </ProjectsGrid>

      <EmailSection>
        <h4>{t("portfolio.interestedInMe")}</h4>
        <a href="mailto:yuanyuanli2022@gmail.com">yuanyuanli2022@gmail.com</a>
      </EmailSection>
    </PortfolioContainer>
  );
};

export default PortfolioContent;
